import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  MapPin, Thermometer, Truck, Calendar, Weight, Phone, MessageCircle, Search, ShieldCheck, Share2, CheckCircle2,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { TRUCK_TYPES, tempTone, waLink, waShare, truckShareMessage, trucksShareAllMessage, fmtDate } from "@/lib/constants";
import { RatingStars } from "@/components/RatingStars";
import RateDialog from "@/components/RateDialog";
import PostTruckDialog from "@/components/PostTruckDialog";
import AccessGate from "@/components/AccessGate";

function TruckCard({ truck, onMessage, onRated, mine }) {
  const wa = waLink(truck.poster_phone, `Hi, is your ${truck.truck_type} at ${truck.current_location} still available?`);
  const markLoaded = async () => {
    try { await api.put(`/trucks/${truck.id}/loaded`); toast.success("Truck marked as loaded"); onRated?.(); }
    catch (e) { console.error("Failed to mark truck loaded:", e); toast.error("Could not update"); }
  };
  return (
    <div data-testid="truck-card" className="rounded-xl border border-border bg-card/60 p-4 sm:p-5 hover:border-primary/40 transition-all duration-300 animate-fadeup">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-center flex-wrap gap-x-2 gap-y-0.5 font-heading font-semibold text-base sm:text-lg min-w-0">
          <Truck className="w-4 h-4 text-primary shrink-0" />
          <span className="break-words">{truck.truck_type}</span>
        </div>
        <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-mono-num border shrink-0 ${tempTone(truck.temp_min)}`}>
          <Thermometer className="w-3 h-3" />{truck.temp_min}° / {truck.temp_max}°
        </span>
      </div>
      <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm text-muted-foreground mb-4">
        <div className="flex items-center gap-1.5 min-w-0"><MapPin className="w-3.5 h-3.5 text-primary/70 shrink-0" /><span className="truncate">{truck.current_location}{truck.destination ? ` → ${truck.destination}` : ""}</span></div>
        <div className="flex items-center gap-1.5"><Weight className="w-3.5 h-3.5 text-primary/70" /><span className="font-mono-num">{truck.capacity}</span> T</div>
        <div className="flex items-center gap-1.5 col-span-2"><Calendar className="w-3.5 h-3.5 text-primary/70" />Available {fmtDate(truck.available_date)}</div>
      </div>
      {truck.notes && <p className="text-sm text-muted-foreground/80 mb-4 line-clamp-2">{truck.notes}</p>}
      <div className="flex flex-wrap items-center justify-between gap-2 pt-3 border-t border-border">
        <div className="text-xs text-muted-foreground truncate flex items-center gap-1.5 min-w-0">
          <span className="text-foreground font-medium truncate">{truck.poster_name}</span>
          {truck.poster_verified && <ShieldCheck className="w-3.5 h-3.5 text-primary shrink-0" title="Verified transporter" />}
          {truck.poster_company ? <span className="truncate">· {truck.poster_company}</span> : null}
          <span className="shrink-0"><RatingStars value={truck.poster_rating} count={truck.poster_rating_count} size={11} /></span>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <a data-testid="truck-share-btn" href={waShare(truckShareMessage(truck))} target="_blank" rel="noreferrer"
             className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border border-border hover:border-[#25D366]/60 hover:text-[#25D366] transition-all">
            <Share2 className="w-3.5 h-3.5" /> Share
          </a>
          {mine && (
            <button data-testid="board-loaded-truck-btn" onClick={markLoaded} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border border-border hover:border-emerald-400/60 hover:text-emerald-300 transition-all">
              <CheckCircle2 className="w-3.5 h-3.5" /> Mark as loaded
            </button>
          )}
          {!mine && <RateDialog userId={truck.user_id} name={truck.poster_name} onRated={onRated} />}
          {!mine && truck.poster_phone && (
            <a data-testid="truck-whatsapp-btn" href={wa} target="_blank" rel="noreferrer"
               className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-[#25D366] text-white hover:brightness-110 transition-all">
              <MessageCircle className="w-3.5 h-3.5" /> WhatsApp
            </a>
          )}
          {!mine && (
            <button data-testid="truck-message-btn" onClick={() => onMessage(truck.user_id)}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border border-border hover:border-primary/50 hover:text-primary transition-all">
              <Phone className="w-3.5 h-3.5" /> Message
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default function TruckFeed() {
  const { auth } = useAuth();
  const navigate = useNavigate();
  const [trucks, setTrucks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ location: "", truck_type: "all" });

  const fetchTrucks = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/trucks", { params: { location: filters.location || undefined, truck_type: filters.truck_type } });
      setTrucks(res.data);
    } catch (e) {
      if (e?.response?.status !== 402) toast.error("Could not load trucks");
    } finally { setLoading(false); }
  }, [filters]);

  useEffect(() => { if (auth?.has_access) fetchTrucks(); else setLoading(false); }, [fetchTrucks, auth]);

  const onMessage = (uid) => navigate(`/app/messages/${uid}`);

  return (
    <AccessGate>
      <div className="max-w-6xl">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="font-heading font-extrabold text-3xl tracking-tight">Truck Board</h1>
            <p className="text-muted-foreground text-sm mt-1">Available reefer trucks ready for freight.</p>
          </div>
          <div className="flex items-center gap-2">
            {trucks.filter((t) => t.user_id === auth?.user?.user_id).length > 0 && (
              <a data-testid="share-all-trucks-btn" href={waShare(trucksShareAllMessage(trucks.filter((t) => t.user_id === auth?.user?.user_id)))} target="_blank" rel="noreferrer"
                 className="inline-flex items-center gap-1.5 px-4 h-10 rounded-full text-sm font-semibold border border-border hover:border-[#25D366]/60 hover:text-[#25D366] transition-all">
                <Share2 className="w-4 h-4" /> Share my trucks
              </a>
            )}
            <PostTruckDialog onCreated={fetchTrucks} />
          </div>
        </div>

        <div className="grid sm:grid-cols-[1fr_240px_auto] gap-3 mb-6 p-4 rounded-xl border border-border bg-card/40">
          <div className="relative">
            <MapPin className="w-4 h-4 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2" />
            <Input data-testid="filter-location" className="pl-9" placeholder="Location" value={filters.location} onChange={(e) => setFilters(f => ({ ...f, location: e.target.value }))} />
          </div>
          <Select value={filters.truck_type} onValueChange={(v) => setFilters(f => ({ ...f, truck_type: v }))}>
            <SelectTrigger data-testid="filter-truck-trucktype"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All truck types</SelectItem>
              {TRUCK_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button data-testid="apply-truck-filters-btn" onClick={fetchTrucks} variant="secondary" className="rounded-lg"><Search className="w-4 h-4 mr-1.5" />Search</Button>
        </div>

        {loading ? (
          <p className="text-muted-foreground text-sm py-16 text-center">Loading trucks…</p>
        ) : trucks.length === 0 ? (
          <div className="text-center py-16 border border-dashed border-border rounded-xl">
            <Truck className="w-10 h-10 text-muted-foreground/50 mx-auto mb-3" />
            <p className="text-muted-foreground">No trucks posted yet. Post your availability first.</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-4">
            {trucks.map((t) => <TruckCard key={t.id} truck={t} mine={t.user_id === auth?.user?.user_id} onMessage={onMessage} onRated={fetchTrucks} />)}
          </div>
        )}
      </div>
    </AccessGate>
  );
}
